<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import ChatMessageSendingJobNewPageTargetSetting, {
  type UserVariableDefinitionType,
} from './ChatMessageSendingJobNewPageTargetSetting/ChatMessageSendingJobNewPageTargetSetting.vue'
import type {
  ChatMessageSendingJobForm,
  ChatMessageSendingJobTarget,
} from './types'
import {
  validateTarget,
  type TargetFormErrors,
  type TargetFormErrorManager,
} from './inputTargetParse'
import { useAlert } from '@/libs/alertPortal'
import ChatMessageSendingJobNewPageQuota from './ChatMessageSendingJobNewPageQuota/ChatMessageSendingJobNewPageQuota.vue'
import { useRoute, useRouter } from 'vue-router'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import { captureException as newrelicCaptureException } from '@/newrelic'
import {
  useChatMessageSendingJobNewPageQuery,
  useScheduleChatMessageSendingJobMutation,
} from './ChatMessageSendingJobNewPage.generated'
import ChatMessageSendingJobNewPageMessageInput from './ChatMessageSendingJobNewPageMessageInput/ChatMessageSendingJobNewPageMessageInput.vue'
import { nanoid } from 'nanoid'
import { catchError } from '@/libs/utilities/catchError'
import { wait } from '@/libs/utilities/wait'
import ChatMessageSendingJobIndexPageSideMenu from '@/pages/ChatMessageSendingJobIndexPage/ChatMessageSendingJobIndexPageSideMenu.vue'
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import { useParseInputMessage } from './inputMessageParse'
import { UserVariableDefinitionDataType } from '@/types/schema.generated'
import { addMinutes, differenceInMinutes } from 'date-fns'
import { toTimeString } from '@/libs/utilities/gql-scalar'
import ChatMessageSendingJobNewPageDateInput from './ChatMessageSendingJobNewPageDateInput/ChatMessageSendingJobNewPageDateInput.vue'

const alert = useAlert()
const router = useRouter()
const route = useRoute('chatMessageSendingJobNew')

const isSubmitting = ref(false)

const form = ref<ChatMessageSendingJobForm>({
  target: { type: 'all' },
  scheduledDate: null,
  messages: [
    {
      id: nanoid(),
      type: 'text',
      text: '',
      selections: undefined,
    },
  ],
})

const targetFormErrors = ref<TargetFormErrors | undefined>()
const showValidate = ref(false)

const MESSAGE_COUNT_LIMIT = 5

const { result: queryResult } = useChatMessageSendingJobNewPageQuery()

const targetFormErrorManager: TargetFormErrorManager = {
  set: (errors: TargetFormErrors | undefined): void => {
    targetFormErrors.value = errors
  },
  getStartsWith: (path: string): string | undefined => {
    const errors = targetFormErrors.value
    if (!errors) {
      return undefined
    }

    const nested = errors.nested
    if (nested === undefined) {
      return undefined
    }

    const keys = Object.keys(nested).filter(key => key.startsWith(path))
    if (keys.length === 0) {
      return undefined
    }

    const key = keys[0] as keyof typeof nested
    if (key === undefined) {
      return undefined
    }

    const errorMessages = nested[key]
    if (errorMessages !== undefined) {
      return errorMessages[0]
    }

    return undefined
  },
}

const addMessage = () => {
  form.value = {
    ...form.value,
    messages: [
      ...form.value.messages,
      {
        id: nanoid(),
        type: 'text',
        text: '',
        selections: undefined,
      },
    ],
  }
}

const moveMessageToPrevious = (messageIndex: number) => {
  if (messageIndex === 0) {
    return
  }

  const newIndex = messageIndex - 1
  const tmp = form.value.messages[newIndex]

  form.value.messages[newIndex] = form.value.messages[messageIndex]
  form.value.messages[messageIndex] = tmp
}

const moveMessageToNext = (messageIndex: number) => {
  if (messageIndex === form.value.messages.length - 1) {
    return
  }

  const newIndex = messageIndex + 1
  const tmp = form.value.messages[newIndex]

  form.value.messages[newIndex] = form.value.messages[messageIndex]
  form.value.messages[messageIndex] = tmp
}

const deleteMessage = (messageIndex: number) => {
  form.value = {
    ...form.value,
    messages: form.value.messages.filter((_, i) => i !== messageIndex),
  }
}

const convertTarget = (target: ChatMessageSendingJobTarget) => {
  if (target.type === 'all') {
    return {
      allUsers: {},
    }
  }
  if (target.type === 'csv') {
    return {
      targetUsers: {
        okanentClientUserIds: target.okanentClientUserIds,
      },
    }
  }
  return {
    segmentUsers: {
      segment: {
        conditions: target.segment.segmentConditions.map(condition => {
          switch (condition.type) {
            case 'line-activation-login-link':
              return {
                loginLinkCondition: {
                  not: condition.not,
                },
              }
            case 'inflow-source':
              return {
                inflowSourceCondition: {
                  inflowSourceDefinitionGUID:
                    condition.inflowSourceDefinitionGUID,
                  not: condition.not,
                },
              }
            case 'click-url':
              return {
                clickURLCondition: {
                  url: condition.url,
                  not: condition.not,
                },
              }
            case 'planner-enquete-answered':
              return {
                enqueteAnsweredCondition: {
                  enqueteGUID: condition.plannerEnqueteGUID,
                  not: condition.not,
                },
              }
            case 'room-status':
              return {
                roomStatusCondition: {
                  status: condition.status,
                  not: condition.not,
                },
              }
            case 'user-variable-definition':
              return {
                userVariableDefinitionCondition: {
                  definitionGUID: condition.definitionGUID,
                  values: [condition.value],
                  not: condition.not,
                },
              }
          }
        }),
      },
    },
  }
}

watch(
  () => form.value.target,
  target => {
    if (!showValidate.value) {
      targetFormErrorManager.set(undefined)
      return
    }
    const convertedTarget = convertTarget(target)
    validateTarget(convertedTarget, targetFormErrorManager.set)
  }
)

const scheduleChatMessageSendingJobMutation =
  useScheduleChatMessageSendingJobMutation()

const parseInputMessages = useParseInputMessage()

class TargetValidationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TargetValidationError'
  }
}

const submitRequest = async (
  form: ChatMessageSendingJobForm
): Promise<string | undefined> => {
  const [error, messages] = await catchError(parseInputMessages(form.messages))
  if (error) {
    alert.error(error.message)
    throw error
  }

  const convertedTarget = convertTarget(form.target)
  const validatedTarget = validateTarget(
    convertedTarget,
    targetFormErrorManager.set
  )
  if (!validatedTarget.success) {
    throw new TargetValidationError('target validation error')
  }

  const res = await scheduleChatMessageSendingJobMutation.mutate({
    input: {
      messages,
      scheduledAt: form.scheduledDate ? toTimeString(form.scheduledDate) : null,
      target: convertedTarget,
    },
  })
  if (res?.errors) {
    throw res.errors
  }

  const data = res?.data?.scheduleChatMessageSendingJob
  if (!data) {
    throw new Error('failed to get res.data')
  }

  if (data.__typename === 'ScheduleChatMessageSendingJobErrorPayload') {
    alert.error(data.reason)
    throw new Error(data.reason)
  }

  if (data.__typename === 'ScheduleChatMessageSendingJobSuccessPayload') {
    return data.jobGuid ?? undefined
  }

  return undefined
}

const handleSubmit = async (e: Event) => {
  e.preventDefault()
  if (!form.value.scheduledDate) {
    if (!window.confirm('メッセージを配信しますか？')) {
      return
    }
  } else if (
    form.value.scheduledDate <= addMinutes(Date.now(), 5) // 5分以内に設定されているとき
  ) {
    const minutes = differenceInMinutes(form.value.scheduledDate, Date.now(), {
      roundingMethod: 'ceil',
    })
    if (
      !window.confirm(
        `メッセージは${minutes}分以内に配信されます。よろしいですか？`
      )
    ) {
      return
    }
  }

  try {
    isSubmitting.value = true
    showValidate.value = true

    const jobGuid = await submitRequest(form.value)

    alert.success('メッセージ配信を登録しました')

    if (jobGuid) {
      // レプリカラグを考慮して1秒待つ
      await wait(1000)

      // eslint-disable-next-line fp/no-mutating-methods
      router.push({
        name: 'chatMessageSendingJobDetail',
        params: {
          entranceGUID: route.params.entranceGUID,
          jobGuid,
        },
      })
    } else {
      alert.error('メッセージ配信の保存に失敗しました')
    }
  } catch (e) {
    console.error(e)

    newrelicCaptureException(e)
    if (e instanceof TargetValidationError) {
      alert.error('ターゲットの設定に誤りがあります')
    } else if (e instanceof Error) {
      alert.error('エラーが発生しました')
    }
  } finally {
    isSubmitting.value = false
  }
}

const userVariableDefinitions = computed<UserVariableDefinitionType[]>(() => {
  if (queryResult.value === undefined) {
    return []
  }

  return queryResult.value.segmentConditionParameters.userVariableDefinitionCondition.userVariableDefinitions
    .map(definition => {
      const value = definition.value
      if (!(
        value?.__typename === 'UserVariableDefinitionValueMultipleSelect' ||
        value?.__typename === 'UserVariableDefinitionValueSelect'
      )) {
        return undefined
      }

      const dataType:
        | typeof UserVariableDefinitionDataType.Select
        | typeof UserVariableDefinitionDataType.MultipleSelect =
        value.__typename === 'UserVariableDefinitionValueSelect'
          ? UserVariableDefinitionDataType.Select
          : UserVariableDefinitionDataType.MultipleSelect

      return {
        dataType,
        guid: definition.guid,
        name: definition.name,
        options: value.options,
      }
    })
    .filter(definition => definition !== undefined)
})

const targetSettingErrorMessage = computed(() =>
  targetFormErrorManager.getStartsWith('targetUsers.segment')
)
</script>

<template>
  <CenteredPageContainer :class="$style.page" wide>
    <div :class="[$style.page__main, $style.setting]">
      <ChatMessageSendingJobIndexPageSideMenu />

      <div :class="$style.wrapper" v-if="queryResult">
        <section :class="$style.container" @submit="handleSubmit">
          <h1>LINEメッセージ配信</h1>
          <ChatMessageSendingJobNewPageQuota
            :style="{ marginTop: '16px' }"
            :fragment="queryResult"
          />
          <hr :class="$style.hr" />
          <div :class="$style.containerInner">
            <ChatMessageSendingJobNewPageTargetSetting
              v-model="form.target"
              :userRegistrationInflowSourceDefinitions="
                queryResult.segmentConditionParameters.inflowSourceCondition
                  .inflowSources
              "
              :clickURLs="
                queryResult.segmentConditionParameters.clickURLCondition
                  .clickUrls
              "
              :plannerEnquetes="
                queryResult.segmentConditionParameters.enqueteAnsweredCondition
                  .enquetes
              "
              :userVariableDefinitions="userVariableDefinitions"
              :errorMessage="targetSettingErrorMessage"
            />
            <section>
              <p :class="$style.sectionTitle">配信日時</p>
              <div>
                <ChatMessageSendingJobNewPageDateInput
                  v-model="form.scheduledDate"
                />
              </div>
            </section>
            <section>
              <p :class="$style.sectionTitle">内容</p>
              <div>
                <div
                  v-for="(_, i) of form.messages"
                  :key="form.messages[i].id"
                  :class="$style.message"
                >
                  <ChatMessageSendingJobNewPageMessageInput
                    v-model="form.messages[i]"
                    :shownControl="form.messages.length > 1"
                    @movePrevious="moveMessageToPrevious(i)"
                    @moveNext="moveMessageToNext(i)"
                    @delete="deleteMessage(i)"
                  />
                </div>
              </div>
              <div
                v-if="form.messages.length < MESSAGE_COUNT_LIMIT"
                :class="$style.addButton"
              >
                <FlatButton @click="addMessage" variant="secondary"
                  >メッセージを追加する
                </FlatButton>
              </div>
            </section>
          </div>
          <FlatButton
            type="submit"
            :disabled="isSubmitting"
            @click="handleSubmit"
            :class="$style.submitButton"
          >
            <LoadingIndicator v-if="isSubmitting" />
            <span v-else>配信を登録する</span>
          </FlatButton>
        </section>
      </div>
    </div>
  </CenteredPageContainer>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  width: 100%;
}

.container {
  background-color: $color-surface-primary;
  padding: 16px;
  border-radius: 10px;
}

.hr {
  border: none;
  border-top: 1px solid $color-border-low-emphasis;
  margin: 32px 0;
}

.containerInner {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.sectionTitle {
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: bold;
}

.message:not(:first-child) {
  border-top: 1px solid $color-border-low-emphasis;
  margin-top: 24px;
  padding-top: 24px;
}

.addButton {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid $color-border-low-emphasis;
}

.submitButton {
  margin-top: 48px;
}

.page {
  padding-bottom: 80px;

  &__header {
    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }

  &__main {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.setting {
  display: grid;
  align-items: flex-start;
  gap: 40px;
  grid-template-columns: 302px 1fr;
  grid-template-rows: auto;
}
</style>
